import React, { useCallback, useEffect, useMemo, useState } from "react";
import Auth from "src/Auth";
import { sendAppEvent } from "../Utils.js";
import { cn } from "src/lib/cn";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { Input } from "src/sudokit/components/Input";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";

type BugReportSeverity = "low" | "medium" | "high" | "critical";
type LinearStatusType = "backlog" | "unstarted" | "started" | "completed" | "canceled" | "triage";

type LinearProject = {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
};

type LinearAssignee = {
  id: string;
  name: string;
};

type LinearMilestone = {
  id: string;
  name: string;
};

type LinearStatus = {
  id: string;
  name: string;
  type: LinearStatusType;
};

type BugReport = {
  id: string;
  chatId: string;
  projectId: string;
  userId: string;
  messageId: string | null;
  invocationId: string | null;
  agentIssueSummary: string;
  userMessage: string;
  diagnosis: string;
  possibleSolutions: string[];
  userExplanation: string | null;
  severity: BugReportSeverity;
  status: string;
  statusType: LinearStatusType;
  linearIssueId: string | null;
  linearIssueIdentifier: string | null;
  linearIssueUrl: string | null;
  createdAt: string;
};

const LIMIT = 50;
const NO_MILESTONE_VALUE = "__no_milestone__";
const UNASSIGNED_VALUE = "__unassigned__";
const DEFAULT_STATUS_VALUE = "__linear_default_status__";

export default function AgenticChatBugReports() {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [severityFilter, setSeverityFilter] = useState<"all" | BugReportSeverity>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDoneCancelled, setShowDoneCancelled] = useState(false);
  const [activeReportForLinear, setActiveReportForLinear] = useState<BugReport | null>(null);
  const [linearProjects, setLinearProjects] = useState<LinearProject[]>([]);
  const [linearAssignees, setLinearAssignees] = useState<LinearAssignee[]>([]);
  const [linearMilestones, setLinearMilestones] = useState<LinearMilestone[]>([]);
  const [linearStatuses, setLinearStatuses] = useState<LinearStatus[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(NO_MILESTONE_VALUE);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(UNASSIGNED_VALUE);
  const [selectedStatusId, setSelectedStatusId] = useState(DEFAULT_STATUS_VALUE);
  const [creatingLinearTicket, setCreatingLinearTicket] = useState(false);
  const [loadingLinearOptions, setLoadingLinearOptions] = useState(false);
  const [linearError, setLinearError] = useState<string | null>(null);

  const dateRangeInvalid = Boolean(startDate && endDate && endDate < startDate);

  useEffect(() => {
    sendAppEvent("Admin Bug Reports - Page Viewed");
  }, []);

  const fetchBugReports = useCallback(async () => {
    if (dateRangeInvalid) {
      setBugReports([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: LIMIT };
      if (severityFilter !== "all") params.severity = severityFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      params.includeDoneCancelled = showDoneCancelled;

      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-bug-reports/list", params);
      if (result.success) {
        setBugReports(result.bugReports);
        setTotal(result.pagination.total);
      }
    } catch (error) {
      console.error("Failed to fetch bug reports", error);
    } finally {
      setLoading(false);
    }
  }, [dateRangeInvalid, endDate, page, severityFilter, showDoneCancelled, startDate]);

  useEffect(() => {
    fetchBugReports();
  }, [fetchBugReports]);

  const loadLinearOptions = useCallback(async () => {
    setLoadingLinearOptions(true);
    setLinearError(null);
    try {
      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-bug-reports/linear/options", {});
      if (result.success) {
        setLinearProjects(result.projects || []);
        setLinearAssignees(result.assignees || []);
      }
    } catch (error) {
      console.error("Failed to fetch Linear options", error);
      setLinearError("Failed to load Linear options.");
    } finally {
      setLoadingLinearOptions(false);
    }
  }, []);

  const loadProjectMetadata = useCallback(async (projectId: string) => {
    if (!projectId) {
      setLinearMilestones([]);
      setLinearStatuses([]);
      setSelectedMilestoneId(NO_MILESTONE_VALUE);
      setSelectedStatusId(DEFAULT_STATUS_VALUE);
      return;
    }

    setLoadingLinearOptions(true);
    setLinearError(null);
    try {
      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-bug-reports/linear/project-metadata", { projectId });
      if (result.success) {
        const milestones: LinearMilestone[] = result.milestones || [];
        const statuses: LinearStatus[] = result.statuses || [];
        setLinearMilestones(milestones);
        setLinearStatuses(statuses);
        setSelectedMilestoneId((prev) =>
          prev === NO_MILESTONE_VALUE || milestones.some((milestone) => milestone.id === prev) ? prev : NO_MILESTONE_VALUE,
        );
        setSelectedStatusId((prev) =>
          prev === DEFAULT_STATUS_VALUE || statuses.some((status) => status.id === prev) ? prev : DEFAULT_STATUS_VALUE,
        );
      }
    } catch (error) {
      console.error("Failed to fetch Linear project metadata", error);
      setLinearError("Failed to load Linear statuses/milestones.");
    } finally {
      setLoadingLinearOptions(false);
    }
  }, []);

  const openLinearModal = async (report: BugReport) => {
    setActiveReportForLinear(report);
    setSelectedProjectId("");
    setSelectedMilestoneId(NO_MILESTONE_VALUE);
    setSelectedAssigneeId(UNASSIGNED_VALUE);
    setSelectedStatusId(DEFAULT_STATUS_VALUE);
    setLinearMilestones([]);
    setLinearStatuses([]);
    await loadLinearOptions();
  };

  const createLinearTicket = async () => {
    if (!activeReportForLinear || !selectedProjectId) return;
    setCreatingLinearTicket(true);
    setLinearError(null);
    try {
      const result = await Auth.fetchWithIdToken("/api/admin/agentic-chat-bug-reports/create-linear-ticket", {
        id: activeReportForLinear.id,
        projectId: selectedProjectId,
        milestoneId: selectedMilestoneId === NO_MILESTONE_VALUE ? null : selectedMilestoneId,
        assigneeId: selectedAssigneeId === UNASSIGNED_VALUE ? null : selectedAssigneeId,
        statusId: selectedStatusId === DEFAULT_STATUS_VALUE ? null : selectedStatusId,
      });

      if (result.success) {
        sendAppEvent("Admin Bug Reports - Linear Ticket Created", {
          bugReportId: activeReportForLinear.id,
          linearIssueId: result.issue?.id || "(unknown)",
        });
        setActiveReportForLinear(null);
        await fetchBugReports();
      }
    } catch (error) {
      console.error("Failed to create Linear ticket", error);
      setLinearError("Failed to create Linear ticket.");
    } finally {
      setCreatingLinearTicket(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT) || 1;
  const showingFrom = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * LIMIT, total);

  const severityDisplay = useMemo<Record<BugReportSeverity, string>>(
    () => ({
      low: "Low",
      medium: "Medium",
      high: "High",
      critical: "Critical",
    }),
    [],
  );

  const selectedProject = useMemo(
    () => linearProjects.find((project) => project.id === selectedProjectId) || null,
    [linearProjects, selectedProjectId],
  );
  const sortedLinearProjects = useMemo(
    () => [...linearProjects].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [linearProjects],
  );
  const sortedLinearMilestones = useMemo(
    () => [...linearMilestones].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [linearMilestones],
  );
  const sortedLinearAssignees = useMemo(
    () => [...linearAssignees].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [linearAssignees],
  );
  const sortedLinearStatuses = useMemo(
    () => [...linearStatuses].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [linearStatuses],
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-6">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto gap-1 flex-col flex">
          <Text className="text-3xl font-semibold bg-gradient-to-r from-app-grey-0 to-app-grey-1 bg-clip-text text-transparent">
            Agentic Chat Bug Reports
          </Text>
          <Text className="text-base text-app-grey-1">
            Review user-submitted bug reports and triage them through in-progress and resolved.
          </Text>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <MenuSelect
            label="Filter by severity"
            value={severityFilter}
            onChange={(value: string) => {
              setSeverityFilter(value as "all" | BugReportSeverity);
              setPage(1);
              sendAppEvent("Admin Bug Reports - Filter Applied", { filterType: "severity", value });
            }}
            options={[
              { value: "all", label: "All severities" },
              { value: "low", label: "Low" },
              { value: "medium", label: "Medium" },
              { value: "high", label: "High" },
              { value: "critical", label: "Critical" },
            ]}
            placeholder="Severity"
          />

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">From</Text>
            <Input
              aria-label="Filter bug reports from date"
              type="date"
              mode="default"
              value={startDate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = event.target.value;
                setStartDate(next);
                setPage(1);
                sendAppEvent("Admin Bug Reports - Filter Applied", {
                  filterType: "startDate",
                  value: next || "(cleared)",
                });
              }}
              className="w-36 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <Text className="text-sm text-app-grey-1">To</Text>
            <Input
              aria-label="Filter bug reports to date"
              type="date"
              mode="default"
              value={endDate}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = event.target.value;
                setEndDate(next);
                setPage(1);
                sendAppEvent("Admin Bug Reports - Filter Applied", {
                  filterType: "endDate",
                  value: next || "(cleared)",
                });
              }}
              className="w-36 text-sm"
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-app-grey-1">
            <input
              aria-label="Toggle done and cancelled bug reports"
              type="checkbox"
              checked={showDoneCancelled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const next = event.target.checked;
                setShowDoneCancelled(next);
                setPage(1);
                sendAppEvent("Admin Bug Reports - Filter Applied", {
                  filterType: "showDoneCancelled",
                  value: next ? "shown" : "hidden",
                });
              }}
            />
            Show done/cancelled
          </label>
        </div>
      </div>

      {dateRangeInvalid ? (
        <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3">
          <Text className="text-sm text-red-400">
            The &quot;To&quot; date must be on or after the &quot;From&quot; date. Adjust the range to load bug reports.
          </Text>
        </div>
      ) : null}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <Spinner size="lg" className="mx-auto mb-4" />
              <Text className="text-sm font-medium text-app-grey-1">Loading bug reports...</Text>
            </div>
          </div>
        ) : dateRangeInvalid ? (
          <div className="text-center py-12 px-4 bg-app-menu-color rounded-xl border border-app-menu-color-hover shadow-card animate-in fade-in">
            <Text className="text-app-grey-1">Fix the date range above to view bug reports.</Text>
          </div>
        ) : bugReports.length === 0 ? (
          <div className="text-center py-12 px-4 bg-app-menu-color rounded-xl border border-app-menu-color-hover shadow-card animate-in fade-in">
            <Text className="text-app-grey-1">No bug reports found matching the current filters.</Text>
          </div>
        ) : (
          <>
            <div className="rounded-xl bg-app-menu-color border border-app-menu-color-hover shadow-card overflow-auto scrollbar-track-transparent scrollbar-corner-transparent scrollbar-thumb-slate-400">
              <table className="min-w-full divide-y divide-app-menu-color-hover">
                <thead className="bg-app-menu-color h-9">
                  <tr className="border-b border-app-menu-color-hover">
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Report ID
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider align-top">
                      Issue Summary
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider align-top">
                      User Message
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider align-top">
                      Diagnosis
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider align-top">
                      Possible Solutions
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider align-top">
                      User Notes
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-app-grey-1 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bugReports.map((report, index) => (
                    <tr
                      key={report.id}
                      className="hover:bg-app-menu-color-hover transition-colors animate-in fade-in slide-in-from-bottom-2 duration-300"
                      style={{ animationDelay: `${Math.min(index * 30, 500)}ms`, animationFillMode: "both" }}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-0">
                        {new Date(report.createdAt).toLocaleDateString()}{" "}
                        {new Date(report.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-app-grey-1">
                        <span className="font-mono">{report.id}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold",
                            report.severity === "critical" && "bg-red-500/20 text-red-500",
                            report.severity === "high" && "bg-orange-500/20 text-orange-400",
                            report.severity === "medium" && "bg-yellow-500/20 text-yellow-400",
                            report.severity === "low" && "bg-green-500/20 text-green-500",
                          )}
                        >
                          {severityDisplay[report.severity]}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold",
                            !report.linearIssueId && "bg-app-grey-3/30 text-app-grey-1",
                            report.linearIssueId && report.statusType === "started" && "bg-blue-500/20 text-blue-400",
                            report.linearIssueId && report.statusType === "completed" && "bg-green-500/20 text-green-500",
                            report.linearIssueId && report.statusType === "canceled" && "bg-orange-500/20 text-orange-400",
                            report.linearIssueId && report.statusType === "triage" && "bg-purple-500/20 text-purple-400",
                            report.linearIssueId && report.statusType === "unstarted" && "bg-app-grey-3/40 text-app-grey-0",
                            report.linearIssueId && report.statusType === "backlog" && "bg-app-grey-3/20 text-app-grey-1",
                          )}
                        >
                          {!report.linearIssueId ? "New (No Linear ticket)" : report.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-app-grey-0 min-w-[14rem] max-w-xl">
                        <div className="break-words whitespace-pre-wrap">{report.agentIssueSummary}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-app-grey-1 min-w-[14rem] max-w-xl">
                        <div className="break-words whitespace-pre-wrap">{report.userMessage}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-app-grey-0 min-w-[14rem] max-w-xl">
                        <div className="break-words whitespace-pre-wrap">{report.diagnosis}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-app-grey-1 min-w-[12rem] max-w-lg">
                        {report.possibleSolutions.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1 break-words">
                            {report.possibleSolutions.map((solution, solutionIndex) => (
                              <li key={`${report.id}-sol-${solutionIndex}`} className="whitespace-pre-wrap">
                                {solution}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-app-grey-2">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-sm text-app-grey-1 min-w-[12rem] max-w-lg">
                        {report.userExplanation ? (
                          <div className="break-words whitespace-pre-wrap">{report.userExplanation}</div>
                        ) : (
                          <span className="text-app-grey-2">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <a
                          href={`/admin/user/${report.userId}`}
                          className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 transition-all"
                        >
                          {report.userId.slice(0, 12)}...
                        </a>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <a
                          href={`/admin/canvas/${report.projectId}`}
                          className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 transition-all"
                        >
                          {report.projectId.slice(0, 12)}...
                        </a>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <SudoButton
                            variant="secondaryGrey"
                            size="small"
                            aria-label={`Create Linear ticket for bug report ${report.id}`}
                            disabled={creatingLinearTicket || Boolean(report.linearIssueId)}
                            onClick={() => openLinearModal(report)}
                          >
                            Create Linear Ticket
                          </SudoButton>
                          {report.linearIssueUrl ? (
                            <a
                              href={report.linearIssueUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-mono text-app-primary-cta-high-contrast hover:brightness-110 transition-all"
                            >
                              {report.linearIssueIdentifier || "Open Linear"}
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4 pb-6">
              <Text className="text-sm text-app-grey-1">
                Showing {showingFrom}–{showingTo} of {total}
              </Text>
              <div className="flex items-center gap-2">
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  aria-label="Go to previous bug reports page"
                  onClick={() => {
                    const nextPage = Math.max(1, page - 1);
                    sendAppEvent("Admin Bug Reports - Page Changed", { page: nextPage });
                    setPage(nextPage);
                  }}
                  disabled={page <= 1}
                >
                  Previous
                </SudoButton>
                <Text className="text-sm text-app-grey-0 px-2">
                  Page {page} of {totalPages}
                </Text>
                <SudoButton
                  variant="secondaryGrey"
                  size="small"
                  aria-label="Go to next bug reports page"
                  onClick={() => {
                    const nextPage = Math.min(totalPages, page + 1);
                    sendAppEvent("Admin Bug Reports - Page Changed", { page: nextPage });
                    setPage(nextPage);
                  }}
                  disabled={page >= totalPages}
                >
                  Next
                </SudoButton>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={Boolean(activeReportForLinear)}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setActiveReportForLinear(null);
            setLinearError(null);
          }
        }}
      >
        <DialogContent aria-label="Create Linear ticket for bug report">
          <DialogHeader>
            <DialogTitle>Create Linear Ticket</DialogTitle>
            <DialogDescription>
              Choose the Linear destination and defaults for this bug report ticket.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-3">
            <MenuSelect
              label="Project"
              value={selectedProjectId}
              onChange={(value: string) => {
                setSelectedProjectId(value);
                void loadProjectMetadata(value);
              }}
              options={sortedLinearProjects.map((project) => ({
                value: project.id,
                label: `${project.name} (${project.teamName})`,
              }))}
              placeholder={loadingLinearOptions ? "Loading projects..." : "Select project"}
            />

            <MenuSelect
              label="Milestone"
              value={selectedMilestoneId}
              onChange={(value: string) => setSelectedMilestoneId(value)}
              options={[
                { value: NO_MILESTONE_VALUE, label: "No milestone" },
                ...sortedLinearMilestones.map((milestone) => ({ value: milestone.id, label: milestone.name })),
              ]}
              placeholder={selectedProject ? "Select milestone (optional)" : "Pick project first"}
            />

            <MenuSelect
              label="Assignee"
              value={selectedAssigneeId}
              onChange={(value: string) => setSelectedAssigneeId(value)}
              options={[
                { value: UNASSIGNED_VALUE, label: "Unassigned" },
                ...sortedLinearAssignees.map((assignee) => ({ value: assignee.id, label: assignee.name })),
              ]}
              placeholder={loadingLinearOptions ? "Loading assignees..." : "Select assignee (optional)"}
            />

            <MenuSelect
              label="Status"
              value={selectedStatusId}
              onChange={(value: string) => setSelectedStatusId(value)}
              options={[
                { value: DEFAULT_STATUS_VALUE, label: "Linear default status" },
                ...sortedLinearStatuses.map((status) => ({
                  value: status.id,
                  label: `${status.name} (${status.type})`,
                })),
              ]}
              placeholder={selectedProject ? "Select status (optional)" : "Pick project first"}
            />

            {linearError ? <Text className="text-sm text-red-400">{linearError}</Text> : null}
          </div>

          <DialogFooter>
            <SudoButton
              variant="tertiaryGrey"
              onClick={() => {
                setActiveReportForLinear(null);
                setLinearError(null);
              }}
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="primary"
              disabled={!selectedProjectId || creatingLinearTicket || loadingLinearOptions}
              onClick={() => void createLinearTicket()}
            >
              {creatingLinearTicket ? "Creating..." : "Create Ticket"}
            </SudoButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
